import * as palette from "@govlaunch/palette";
import { ProjectCard } from "@govlaunch/project";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import { FormattedMessage } from "react-intl";
import useIsMobile from "~/lib/hooks/useIsMobile";
import { IGovernmentQuery } from "~/lib/queries/__generated__/GovernmentQuery.generated";
import { IProjectsByGovernmentQuery } from "~/lib/queries/__generated__/ProjectsByGovernmentQuery.generated";
import { useIntlLocale } from "~/lib/withIntl";
import TranslationButton from "~/src/components/translation/TranslationButton";
import { appendNextLinkHrefParams } from "~/src/utils/nextLinkHref";
import mediaQueries from "~/components/mediaQueries";

interface IProjectFromOtherGovernmentsProps {
  government: NonNullable<IGovernmentQuery["government"]>;
  projectsByGovernment: NonNullable<IProjectsByGovernmentQuery["projectsByGovernment"]>;
}

export default function ProjectFromOtherGovernments({
  government,
  projectsByGovernment,
}: IProjectFromOtherGovernmentsProps) {
  const router = useRouter();
  const isMobile = useIsMobile();
  const { locale } = useIntlLocale();

  if (!projectsByGovernment.items) {
    return null;
  }
  const otherProjects = government.relatedProjects.slice(0, 6 - projectsByGovernment.items.length);
  if (!otherProjects || otherProjects.length === 0) {
    return null;
  }

  return (
    <div
      css={{
        display: "grid",
        gridRowGap: 24,
      }}
    >
      <h2
        css={{
          margin: 0,
          color: palette.sealBlue,
          fontWeight: 400,
          fontSize: 16,
        }}
      >
        <span>
          <FormattedMessage defaultMessage="Other projects in Govlaunch" id="5scVcr" />
        </span>
      </h2>

      <div
        css={mediaQueries({
          display: "grid",
          gridTemplateColumns: ["1fr", "1fr 1fr"],
          gridColumnGap: 16,
          gridRowGap: 16,
        })}
      >
        {otherProjects.map((project) => {
          const linkHref = isMobile
            ? `/projects/${project.slug}`
            : appendNextLinkHrefParams(router, {
                projectSlug: project.slug,
              });

          const translationLinkHref = isMobile
            ? `/projects/${project.slug}/${locale}`
            : appendNextLinkHrefParams(router, {
                projectSlug: project.slug,
                lang: locale,
              });

          const translationLinkWrap = () => {
            return (
              <TranslationButton
                paddingY="4px"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  router.push(translationLinkHref);
                }}
              />
            );
          };

          return (
            <Link
              href={linkHref}
              as={isMobile ? `/projects/${project.slug}` : `/governments/${government.slug}/projects/${project.slug}`}
              passHref={true}
              key={project._id}
              scroll={false}
            >
              <ProjectCard
                project={project}
                translationLinkWrap={locale !== "en-US" ? translationLinkWrap : undefined}
              />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
